// Firebase Configuration - SECURE: NO hardcoded credentials
export const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "",
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL || "",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || ""
};

// Firebase Functions Configuration - SECURE: NO hardcoded credentials
export const firebaseFunctionsConfig = {
  region: process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION || "",
  endpoint: process.env.REACT_APP_FIREBASE_FUNCTIONS_ENDPOINT || ""
};

// Cache the environment to prevent race conditions and ensure consistency
let cachedEnvironment: 'dev' | 'prod' | null = null;

// Environment Configuration
export const getEnvironment = (): 'dev' | 'prod' => {
  // Return cached environment if available (prevents race conditions)
  if (cachedEnvironment !== null) {
    return cachedEnvironment;
  }

  // Priority 1: Hostname detection (most reliable for deployed apps)
  // This should always work on first load since window.location is immediately available
  if (typeof window !== 'undefined' && window.location) {
    const hostname = window.location.hostname;
    const fullUrl = window.location.href;
    
    // Check for production hostnames (custom domain first, then Firebase hosting)
    if (hostname === 'fitzaa.life' || hostname === 'fitzaa-prod.web.app' || hostname === 'fitzaa-prod.firebaseapp.com') {
      cachedEnvironment = 'prod';
      console.log('🔧 [web] Production hostname detected - FORCING prod environment:', hostname);
      return cachedEnvironment;
    } 
    // Check for development hostnames (custom domain first, then Firebase hosting)
    else if (hostname === 'dev.fitzaa.life' || hostname === 'fitzaa-dev.web.app' || hostname === 'fitzaa-dev.firebaseapp.com' || hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
      cachedEnvironment = 'dev';
      console.log('🔧 [web] Development hostname detected - FORCING dev environment:', hostname);
      return cachedEnvironment;
    }
    // If hostname contains 'prod' but not 'dev', assume prod
    else if (hostname.includes('prod') && !hostname.includes('dev')) {
      cachedEnvironment = 'prod';
      console.log('🔧 [web] Production-like hostname detected - FORCING prod environment:', hostname);
      return cachedEnvironment;
    }
  }

  // Priority 2: Check build-time environment variable (baked into the build)
  // This is set during build via REACT_APP_ENVIRONMENT
  if (process.env.REACT_APP_ENVIRONMENT) {
    cachedEnvironment = process.env.REACT_APP_ENVIRONMENT as 'dev' | 'prod';
    console.log('🔧 [web] Using build-time environment variable:', cachedEnvironment);
    return cachedEnvironment;
  }

  // Final fallback to dev for safety
  cachedEnvironment = 'dev';
  console.warn('⚠️ [web] No environment detection possible, defaulting to dev for safety');
  return cachedEnvironment;
};

// Debug mode configuration
export const isDebugMode = (): boolean => {
  return process.env.REACT_APP_DEBUG === 'true' || process.env.REACT_APP_DEBUG === '1';
};

// Database Path Configuration (Most Popular Approach)
export const getDatabasePath = (path: string): string => {
  const environment = getEnvironment();
  const basePath = environment === 'dev' ? 'dev' : 'prod';
  return `${basePath}/${path}`;
};

// Database Paths for Common Collections
export const databasePaths = {
  users: () => getDatabasePath('users'),
  challenges: () => getDatabasePath('challenges'),
  organizations: () => getDatabasePath('organizations'),
  teams: () => getDatabasePath('teams'),
  leaderboards: () => getDatabasePath('leaderboards'),
  notifications: () => getDatabasePath('notifications'),
  settings: () => getDatabasePath('settings'),
  teamsPhotos: () => getDatabasePath('teamsPhotos')
};

// Debug Firebase config (only in debug mode)
if (isDebugMode()) {
  console.log('[Firebase Config] Initializing with config:', {
    apiKey: firebaseConfig.apiKey ? 'SET' : 'NOT SET',
    authDomain: firebaseConfig.authDomain,
    projectId: firebaseConfig.projectId,
    appId: firebaseConfig.appId,
    isWebApp: firebaseConfig.appId?.includes(':web:'),
    environment: getEnvironment(),
    databasePaths: {
      users: databasePaths.users(),
      challenges: databasePaths.challenges()
    }
  });
} else {
  console.log('[Firebase Config] Initializing with config:', {
    apiKey: firebaseConfig.apiKey ? 'SET' : 'NOT SET',
    environment: getEnvironment(),
    databasePaths: {
      users: databasePaths.users(),
      challenges: databasePaths.challenges()
    }
  });
}

// Azure AD Configuration - Now secured via Firebase Functions
// Use secureConfigService.getAzureADConfig() instead
export const azureADConfig = {
  // Configuration moved to secure Firebase Functions
  // Use: await secureConfigService.getAzureADConfig()
  clientId: "", // Will be loaded from secure function
  authority: "", // Will be loaded from secure function
  redirectUri: "" // Will be loaded from secure function
};

// App Configuration
export const appConfig = {
  name: process.env.REACT_APP_APP_NAME || "Fitzaa",
  version: process.env.REACT_APP_APP_VERSION || "1.0.0"
}; 